import { useState } from "react";
import RegularLoader from "../../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../../config/api";

export default function ApproveRejectModal({
  claim,
  isAdmin,
  isCompany,
  onClose,
  onAssign,
  onApprove,
  onReject,
}) {
  const [mode, setMode] = useState("view"); // view | reject | approve
  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [remark, setRemark] = useState("");

  const authUserString = localStorage.getItem("authUserData");
const authUser = authUserString ? JSON.parse(authUserString) : null;

const isRetailerMobile =
  window.innerWidth < 640 && authUser?.role === 5;

  if (!claim) return null;

  /* ---------------- FETCH REASONS ---------------- */
  const fetchReasons = async (reasonType) => {
    try {
      setLoadingReasons(true);
      const res = await fetch(
        `${BASE_URL}warranty/claim-reasons`
      );
      const json = await res.json();

      setReasons(
        json?.data?.filter((r) => r.reason_type === reasonType) || []
      );
    } catch (err) {
      console.error(err);
      setReasons([]);
    } finally {
      setLoadingReasons(false);
    }
  };

  const photoUrl = (p) =>
    p ? `https://zoho.goelectronix.in/storage/${p}` : "";

  const StatusBadge = ({ status }) => (
    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700">
      {status}
    </span>
  );

  /* ================= STATUS CHECK ================= */
const normalizedStatus = String(claim.status || "")
  .toLowerCase()
  .replaceAll(" ", "_");

// Treat both as pending
const isPending = ["pending"].includes(normalizedStatus);


  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/10 shadow-[0_-10px_40px_rgba(0,0,0,0.35)] backdrop-blur-sm">

  

      <div
        className={`
          w-full max-w-4xl max-h-[90vh] rounded-3xl flex flex-col overflow-hidden 
          ${
            isRetailerMobile
              ? "bg-black/10 backdrop-blur-2xl border border-white/30 shadow-[0_25px_60px_rgba(31,38,135,0.35)]"
              : "bg-white shadow-xl"
          }
        `}
      >

        {/* ================= HEADER ================= */}
        <div className={`px-6 py-4 border-b flex justify-between items-center shrink-0
          ${
            isRetailerMobile
              ? "border-white/20"
              : ""
          }
        `}>
          <div>
            <h2 className="text-lg font-semibold">
              {isAdmin ? "Verify Claim" : "Claim Details"}
            </h2>

            <p className="text-sm text-slate-500">
              Review details before taking action
            </p>
          </div>
          <button
            onClick={onClose}
            className={`
              text-xl transition
              ${
                isRetailerMobile
                  ? "text-white/70 hover:text-white"
                  : "text-slate-400 hover:text-slate-700"
              }
            `}
          >
            ✕
          </button>
        </div>

        {/* ================= BODY (SCROLLABLE) ================= */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">

          {/* ---------- SUMMARY ---------- */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Summary label="Claim ID" value={claim.claim_code} />
            <Summary label="Status" value={<StatusBadge status={claim.status} />} />
            <Summary label="Estimate" value={`₹ ${claim.estimate_amount}`} highlight />
            <Summary label="Payable" value={`₹ ${claim.payable_amount}`} highlight />
          </div>

          {/* ---------- CUSTOMER & DEVICE ---------- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Card title="Customer Details">
              <Info label="Name" value={claim.customer?.name} />
              <Info label="Mobile" value={claim.customer?.mobile} />
              <Info label="Email" value={claim.customer?.email} />
            </Card>

            <Card title="Device Details">
              <Info label="Product" value={claim.device?.product_name} />
              <Info label="IMEI" value={claim.device?.imei1} />
              <Info label="Issue" value={claim.issue_description} />
            </Card>
          </div>

          {/* ---------- INSPECTION ---------- */}
          <Card title="Inspection & Coverage">
            <Info
              label="Inspection Report"
              value={claim.inspection_report || "—"}
            />

            {claim.coverages?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-slate-500 mb-1">Coverages</p>
                <div className="flex gap-2 flex-wrap">
                  {claim.coverages.map((c) => (
                    <span
                      key={c.id}
                      className="px-2 py-1 text-sm rounded"
                    >
                      {c.coverage_title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* ---------- PHOTOS ---------- */}
          {claim.photos?.length > 0 && (
            <Card title="Uploaded Photos">
              <div className="grid grid-cols-6 gap-3">
                {claim.photos.map((p) => (
                  <a
                    key={p.id}
                    href={photoUrl(p.photo_path)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={photoUrl(p.photo_path)}
                      alt=""
                      className="h-24 w-24 object-cover rounded-lg border hover:scale-105 transition"
                    />
                  </a>
                ))}
              </div>
            </Card>
          )}

          {/* ---------- APPROVE / REJECT DETAILS ---------- */}
          {(mode === "reject" || mode === "approve") && (
            <Card title={mode === "reject" ? "Reject Details" : "Approval Details"}>
              {loadingReasons ? (
                <RegularLoader />
              ) : (
                <select
                  className="border w-full px-3 py-2 rounded-md mb-3"
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                >
                  <option value="">Select reason</option>
                  {reasons.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title}
                    </option>
                  ))}
                </select>
              )}

              <textarea
                rows={3}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder={
                  mode === "reject"
                    ? "Enter rejection remarks (optional)"
                    : "Enter approval remarks (optional)"
                }
                className="border w-full px-3 py-2 rounded-md resize-none text-sm"
              />
            </Card>
          )}
        </div>

       {/* ================= FOOTER ================= */}
        <div
          className={`
            px-6 py-4 border-t flex justify-end gap-3 shrink-0
            ${
              isRetailerMobile
                ? "border-white/20 bg-white/5 backdrop-blur-xl"
                : "bg-slate-50"
            }
          `}
        >

        {/* CANCEL — ALWAYS */}
        <button
            onClick={onClose}
            className={`
              px-5 py-2 text-sm rounded-xl transition
              ${
                isRetailerMobile
                  ? "bg-white/10 border border-white/30 text-slate-900"
                  : "border"
              }
            `}
        >
            Cancel
        </button>

        {/* ASSIGN — ONLY WHEN APPROVED */}
        {isCompany && normalizedStatus === "approved" && (
            <button
            onClick={onAssign}
            className="bg-blue-600 text-white px-4 py-2 text-sm rounded-md"
            >
            Assign Employee
            </button>
        )}

        {/* ADMIN ACTIONS — ONLY WHEN PENDING */}
        {isAdmin && isPending && mode === "view" && (
            <>
            <button
                onClick={() => {
                setMode("reject");
                setSelectedReason("");
                setRemark("");
                fetchReasons("reject");
                }}
                className="bg-red-600 text-white px-4 py-2 text-sm rounded-md"
            >
                Reject
            </button>

            <button
                onClick={() => {
                setMode("approve");
                setSelectedReason("");
                setRemark("");
                fetchReasons("approve");
                }}
                className="bg-green-600 text-white px-4 py-2 text-sm rounded-md"
            >
                Approve
            </button>
            </>
        )}

        {/* CONFIRM REJECT — ONLY WHEN PENDING */}
        {isAdmin && isPending && mode === "reject" && (
            <button
            disabled={!selectedReason}
            onClick={() => onReject(claim, selectedReason, remark)}
            className="bg-red-600 disabled:bg-red-300 text-white px-4 py-2 text-sm rounded-md"
            >
            Confirm Reject
            </button>
        )}

        {/* CONFIRM APPROVE — ONLY WHEN PENDING */}
        {isAdmin && isPending && mode === "approve" && (
            <button
            disabled={!selectedReason}
            onClick={() => onApprove(claim, selectedReason, remark)}
            className="bg-green-600 disabled:bg-green-300 text-white px-4 py-2 text-sm rounded-md"
            >
            Confirm Approve
            </button>
        )}
        </div>

      </div>
    
    </div>
  );
}

/* ================= UI HELPERS ================= */

function Card({ title, children }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const isRetailerMobile =
    window.innerWidth < 640 && authUser?.role === 5;

  return (
    <div
      className={`
        rounded-2xl p-4 space-y-2 transition-all
        ${
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-xl border border-white/30 text-slate-900"
            : "border bg-white"
        }
      `}
    >
      <h4 className="text-sm font-semibold">
        {title}
      </h4>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="font-medium text-slate-800">{value || "—"}</p>
    </div>
  );
}

function Summary({ label, value, highlight }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const isRetailerMobile =
    window.innerWidth < 640 && authUser?.role === 5;

  return (
    <div
      className={`
        rounded-2xl p-3 transition-all
        ${
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-xl border border-white/30"
            : "border bg-white"
        }
      `}
    >
      <p className="text-sm opacity-70">{label}</p>
      <div
        className={`mt-1 font-semibold ${
          highlight ? "text-blue-600 text-lg" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}
